import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Card } from '../components/Card';
import { LoadingScreen } from './LoadingScreen';
import { OtpForm } from '../components/auth/OtpForm';
import { useAuth } from '../contexts/AuthContext';
import { setAccessToken } from '../lib/api';
import { safeInternalPath } from '../lib/internalPath';

// `?next=…` lets us return the user to the URL they tried to load while
// unauthenticated. AuthGate sets it; we honour it on successful verify.
function parseNextParam(search: string): string {
  // The guard itself lives in lib/internalPath.ts — `?backTo=` (#1105) needs the
  // same one, and two copies of a redirect check means hardening one of them.
  return safeInternalPath(new URLSearchParams(search).get('next'), '/cases');
}

// Login page chrome only. The OTP email → code → verify flow lives in the
// shared <OtpForm> (components/auth/OtpForm.tsx) so the session-expired modal
// reuses the exact same form — this is an extraction, not a removal.
export function LoginScreen() {
  const { userEmail, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const next = parseNextParam(location.search);

  // Restoration may still establish another account. Keep OTP unavailable
  // until that decision finishes, just as AuthGate waits before showing routes.
  if (authLoading) return <LoadingScreen />;

  // Already authed → bounce to the intended URL. Placed AFTER all hook calls
  // so a conditional early return never changes hook count between renders
  // (silent-refresh flips `userEmail` from null → string mid-lifecycle).
  if (!authLoading && userEmail) {
    return <Navigate to={next} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-navy">
      <Card className="w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-8">
          <Logo size="lg" className="mb-2" />
        </div>

        <OtpForm
          onSuccess={(token) => {
            // verifyOtp already set auth state; re-set defensively and land
            // back on the originally requested URL.
            setAccessToken(token);
            navigate(next, { replace: true });
          }}
        />
      </Card>
    </div>
  );
}
